import { createSlice } from "@reduxjs/toolkit";

/**
 * @name @createSlice
 * @description create redux store slice for Appointment
 **/

import OutlookIcon from "@/public/assets/app-images/outlook-icon.svg";
import GoogleIcon from "@/public/assets/app-images/google-icon.svg";
import { Enums } from "@/services/dropdown-enums";

const platforms = [
  {
    id: Enums.EmailProviderType.microsoft,
    value: "MICROSOFT",
    label: "Outlook",
    name: "outlook",
    icon: OutlookIcon.src,
  },
  {
    id: Enums.EmailProviderType.google,
    value: "GMAIL",
    label: "Google",
    name: "google",
    icon: GoogleIcon.src,
  },
];
export const initialEventDetailsState = {
  // id: 0,
  // uuid: 0,
  openingHours: [],
  name: "",
  timeZoneId: "",
  description: "",
  assignmentType: Enums.ScheduleServiceTypes.RoundRobin,
  duration: -1,
  durationUnit: "",
  minimumBookingNotice: -1,
  minimumBookingNoticeUnit: "",
  minimumCancelationNotice: -1,
  minimumCancelationNoticeUnit: "",
  futureLimit: -1,
  futureLimitUnit: "",
  bufferTime: -1,
  bufferTimeUnit: "",
  bookingLimit: -1,
  members: [],
  reponseHandled: false,
  reminderChannelStatuses: [
    { channelType: Enums.CustomerChannels.InAppNotifications },
  ],
  appointmentReminders: [],
  isReminderUpdate: true,
};

export const AppointmentSlice = createSlice({
  name: "appointment",
  initialState: {
    appointments: [],
    filteredAppointments: [],
    selectedAppointment: null,
    appointmentType: [],
    serviceTypes: [],
    members: [],
    loading: false,
    switchType: "serviceTypes",
    switchView: "calendar",
    platforms: platforms,
    settingsActiveTab: 1,
    appointmentSettings: {},
    eventDetails: initialEventDetailsState,
    calendars: [],
    isCalendarsFetched: false,
    calendar: {},
    tempStatusPayload: null,
  },

  reducers: {
    setLoadingToState: (state, action) => {
      state.loading = action.payload;
    },
    setAppointmentsToState: (state, action) => {
      state.appointments = action.payload;
      state.filteredAppointments = action.payload;
    },
    setTempStatusPayload: (state, action) => {
      state.tempStatusPayload = action.payload;
    },
    setAppointmentToState: (state, action) => {
      let allAppointments = [...state.appointments];
      allAppointments.push(action.payload);
      state.appointments = allAppointments;
      state.filteredAppointments = allAppointments;
    },
    updateAppointmentToState: (state, action) => {
      let data = action.payload;
      let allAppointments = JSON.parse(JSON.stringify(state.appointments));
      let index = allAppointments.findIndex((item) => item?.id === data?.id);
      if (index !== -1) {
        allAppointments[index] = data;
      }

      state.appointments = allAppointments;
    },
    removeAppointment: (state, action) => {
      let { id } = action.payload;
      let allAppointments = JSON.parse(JSON.stringify(state.appointments));
      allAppointments = allAppointments.filter((item) => item?.id !== id);
      state.appointments = allAppointments;
      state.filteredAppointments = allAppointments;
    },
    setSelectedAppointmentToState: (state, action) => {
      state.selectedAppointment = action.payload;
    },
    setServiceTypesToState: (state, action) => {
      state.serviceTypes = action.payload;
    },
    setMembersToState: (state, action) => {
      state.members = action.payload;
    },
    setAppointmentTypeToState: (state, action) => {
      let type = action.payload;
      state.appointmentType = type;
      let switchMode =
        state.switchType === "serviceTypes" ? "calendarId" : "memberId";
      let temp = [...state.appointments];
      if (type.length) {
        temp = temp.filter((event) => type.includes(event[switchMode]));
      } else {
        temp = [];
      }

      state.filteredAppointments = temp;
    },
    setSwitchTypeToState: (state, action) => {
      state.switchType = action.payload;
    },
    setSwitchViewToState: (state, action) => {
      state.switchView = action.payload;
    },
    setSettingsActiveTab: (state, action) => {
      state.settingsActiveTab = action.payload;
    },
    setAppointmentSettings: (state, action) => {
      const { key = "", value = null } = action.payload;
      if (key && value) {
        state.appointmentSettings[key] = value;
      } else {
        state.appointmentSettings = action.payload;
      }
    },
    setCalendarsToState: (state, action) => {
      state.calendars = action.payload;
    },
    setCalendarsIsFetchedToState: (state, action) => {
      state.isCalendarsFetched = action.payload;
    },
    setCalendarToState: (state, action) => {
      state.calendar = action.payload;
    },
    setReponseHandled: (state, action) => {
      state.reponseHandled = action.payload;
    },
    setAppointmentSliceInitialState: (state) => {
      state.calendars = [];
      state.calendar = {};
      state.members = [];
      state.serviceTypes = [];
      state.appointments = [];
      state.filteredAppointments = [];
      state.selectedAppointment = {};
      state.isCalendarsFetched = false;
    },
  },
});

export const {
  setLoadingToState,
  setAppointmentsToState,
  setAppointmentToState,
  updateAppointmentToState,
  removeAppointment,
  setSelectedAppointmentToState,
  setServiceTypesToState,
  setMembersToState,
  setAppointmentTypeToState,
  setSwitchTypeToState,
  setSwitchViewToState,
  setSettingsActiveTab,
  setAppointmentSettings,
  setCalendarsToState,
  setCalendarToState,
  setCalendarsIsFetchedToState,
  setReponseHandled,
  setAppointmentSliceInitialState,
  setTempStatusPayload,
} = AppointmentSlice.actions;

/* called a selector and allows to select values from the state */

export const getLoading = (state) => {
  return state.appointment.loading;
};

export const getAppointments = (state) => {
  return state.appointment.appointments;
};

export const getSelectedAppointment = (state) => {
  return state.appointment.selectedAppointment;
};

export const getServiceTypes = (state) => {
  return state.appointment.serviceTypes;
};

export const getMembers = (state) => {
  return state.appointment.members;
};
export const getFilteredAppointments = (state) => {
  return state.appointment.filteredAppointments;
};

export const getAppointmentType = (state) => {
  return state.appointment.appointmentType;
};
export const getSwitchType = (state) => {
  return state.appointment.switchType;
};
export const getSwitchView = (state) => {
  return state.appointment.switchView;
};
export const getPlatforms = (state) => {
  return state.appointment.platforms;
};
export const getSettingsActiveTab = (state) => {
  return state.appointment.settingsActiveTab;
};
export const getAppointmentSettings = (state) => {
  return state.appointment.appointmentSettings;
};
export const getAppointmentSettingsDetails = (state) => {
  return state.appointment.appointmentSettings.details;
};
export const getCalendars = (state) => {
  return state.appointment.calendars;
};
export const getCalendarsCallState = (state) => {
  return state.appointment.isCalendarsFetched;
};
export const getCalendar = (state) => {
  return state.appointment.calendar;
};
export const getReponseHandled = (state) => {
  return state.appointment.reponseHandled;
};
export const getTempStatusPayload = (state) => {
  return state.appointment.tempStatusPayload;
};

export default AppointmentSlice.reducer;
